/**
 * Engraving close-up (companion to component-engraving-closeup.css)
 *
 * Gives the shopper a proofreading view of the engraving: the print area
 * cropped out of Tee in Blue's mockup and blown up until the lettering is
 * actually readable, opened by click or tap from a button under the preview.
 *
 * Why this is not TIB's hover zoom
 * --------------------------------
 * TIB ships a hover zoom, but it renders the *whole* mockup at a hardcoded
 * 1000px, so its magnification is `1000 / stage width` — and it is driven by
 * `mouseover`, so on a phone or a tablet it does not exist. Cropping to the
 * print area escapes both limits at once.
 *
 * Why a clone and not our own geometry
 * ------------------------------------
 * TIB positions its print-area overlays with computed inline pixel geometry
 * relative to the mockup box, including a `matrix3d` on the artwork layer that
 * bends the engraving to the curve of the urn. That curve is theirs to own.
 * So this clones `.tee-mockup-item` whole and applies a single uniform
 * transform to the copy: photograph, overlay and curve all scale together and
 * cannot desync.
 *
 * The lettering itself never goes soft. TIB's artwork lives in one
 * document-level `<svg id="tee-artwork-...">` that every mockup copy points at
 * with `<use href="...">`, so the clone renders live vector text at whatever
 * size we scale it to — and, usefully, re-renders on its own the moment the
 * shopper edits a line, because it is referencing the same element TIB is
 * updating.
 *
 * Resilience
 * ----------
 * This is progressive enhancement in the same shape as
 * `tee-in-blue-preview.js`: `readSource()` returns null at every point the DOM
 * is not worth reading — TIB absent, gallery not taken over, engraving turned
 * off, geometry not laid out yet — and the caller responds by hiding (or never
 * creating) the control. There is no state in which a dead button is shown.
 */
(function () {
	'use strict'

	// TIB adds `tee-gallery-wrapper` to the product media column when it takes
	// that column over. It does so on the shopper's first interaction with the
	// personalization form, not at page load, so this cannot be read once — it
	// has to be watched for.
	var COLUMN_SELECTOR = '.product__main'
	var ACTIVE_SELECTOR = '.product__main.tee-gallery-wrapper'

	// The on-screen copy of the mockup. The other copy lives in
	// `.tee-hover-zoom-zoomer` at a fixed 1000px and is not what the shopper is
	// looking at.
	var ITEM_SELECTOR = '.tee-hover-zoom-normal .tee-mockup-item'
	var ITEM_FALLBACK_SELECTOR = '.tee-mockup-item'

	// The engraving layer. Both engraving paths — Text-Only and Text + Clipart
	// — put everything inside this one element (clipart is an extra layer
	// *within* the shared artwork SVG, not a second print area), but products
	// with more than one print area exist, so take the union of all of them.
	var ARTWORK_SELECTOR = '.tee-printarea--artwork'

	// TIB keeps the engraving artwork itself in a hidden staging container at the
	// end of <body>, *outside* the product column, and the print area in the
	// column only points at it. So filling in or emptying an engraving produces
	// no mutation inside the column at all — watching the column alone means
	// missing the exact moment the close-up becomes available.
	var TIB_ROOT_SELECTOR = '#teeinblue-wrapper'

	var MODAL_ID = 'EngravingCloseupModal'
	var CLOSE_ID = 'ModalClose-EngravingCloseup'
	var TRIGGER_ID = 'EngravingCloseupTrigger'

	// A print area larger than the mockup that contains it is not a print area,
	// it is a frame mid-render. Reject the reading rather than crop to garbage.
	var MAX_AREA_FRACTION = 1.5

	/**
	 * Coalesces any number of calls within a frame into one. TIB rewrites its
	 * inline geometry on every keystroke, so the observers below fire in bursts.
	 */
	function frameDebounce(fn) {
		var pending = null

		return function () {
			if (pending) return
			pending = requestAnimationFrame(function () {
				pending = null
				fn()
			})
		}
	}

	/* -----------------------------------------------------------------------
	   Geometry
	   -------------------------------------------------------------------- */

	function isRendered(el) {
		var rect = el.getBoundingClientRect()
		return rect.width > 0 && rect.height > 0
	}

	/**
	 * Whether an artwork layer actually has an engraving in it.
	 *
	 * This is not the same question as "is the element there". When the shopper
	 * sets Engraving to "No", TIB leaves the print-area element in place at full
	 * size and simply empties the artwork it points at — so measuring geometry
	 * alone would happily offer a close-up of a blank patch of urn. The content
	 * lives in one document-level `<svg id="tee-artwork-...">` that the print
	 * area references with `<use>`, and that is what has to be checked.
	 */
	function hasArtwork(area) {
		var svg = area.querySelector('svg')
		if (!svg) return false

		var uses = svg.querySelectorAll('use')
		// No indirection: whatever is drawn is drawn here. TIB's Artwork 2.0 already
		// renders parts of the form differently from 1.0 (see the
		// `.tee-custom-select__current` fallback in `assets/global.js`), so not
		// assuming one shape of the preview DOM either is cheap insurance.
		if (!uses.length) return !!svg.querySelector('g, path, image, text')

		for (var i = 0; i < uses.length; i++) {
			var href =
				uses[i].getAttribute('href') || uses[i].getAttribute('xlink:href')
			if (!href || href.charAt(0) !== '#') continue
			var target = document.getElementById(href.slice(1))
			// An artwork SVG stripped back to an empty `<defs>` counts as empty.
			if (target && target.querySelector('g, path, image, text')) return true
		}

		return false
	}

	/**
	 * Union of the bounding rectangles of every artwork layer that is both
	 * rendered and carrying an engraving, in viewport coordinates. Bounding
	 * rectangles rather than layout boxes deliberately: the artwork layer
	 * carries a `matrix3d`, so its layout box and the pixels the shopper sees
	 * are not the same rectangle, and it is the pixels we are cropping to.
	 */
	function artworkBounds(item) {
		var areas = item.querySelectorAll(ARTWORK_SELECTOR)
		var left = Infinity
		var top = Infinity
		var right = -Infinity
		var bottom = -Infinity
		var found = false

		for (var i = 0; i < areas.length; i++) {
			if (!isRendered(areas[i])) continue
			if (!hasArtwork(areas[i])) continue
			var rect = areas[i].getBoundingClientRect()
			if (rect.left < left) left = rect.left
			if (rect.top < top) top = rect.top
			if (rect.right > right) right = rect.right
			if (rect.bottom > bottom) bottom = rect.bottom
			found = true
		}

		if (!found) return null
		return { left: left, top: top, width: right - left, height: bottom - top }
	}

	function liveMockup() {
		var column = document.querySelector(ACTIVE_SELECTOR)
		if (!column) return null
		return (
			column.querySelector(ITEM_SELECTOR) ||
			column.querySelector(ITEM_FALLBACK_SELECTOR)
		)
	}

	/**
	 * The live mockup and its print area, or null when the DOM is not in a
	 * state worth reading. Every early return here is a state the shopper can
	 * genuinely be in — TIB still loading, engraving set to "No", the gallery
	 * re-rendering after an option change — so none of them is an error.
	 */
	function readSource() {
		var item = liveMockup()
		if (!item) return null

		var itemRect = item.getBoundingClientRect()
		if (!itemRect.width || !itemRect.height) return null
		if (!item.offsetWidth || !item.offsetHeight) return null

		var bounds = artworkBounds(item)
		if (!bounds) return null
		if (!bounds.width || !bounds.height) return null
		if (
			bounds.width / itemRect.width > MAX_AREA_FRACTION ||
			bounds.height / itemRect.height > MAX_AREA_FRACTION
		) {
			return null
		}

		return { item: item, bounds: bounds }
	}

	/**
	 * True only for the one unreadable state that is a decision rather than a
	 * hiccup: TIB is showing a print area and the shopper has emptied it, i.e.
	 * set Engraving to "No".
	 *
	 * Worth separating out because the other ways `readSource()` comes back null
	 * are transient — mid-re-render, or the 0x0 stage TIB collapses to when the
	 * window is resized without a reload. Tearing the close-up down on those
	 * would make a resize look like a crash.
	 */
	function engravingRemoved() {
		var item = liveMockup()
		if (!item) return false

		var areas = item.querySelectorAll(ARTWORK_SELECTOR)
		if (!areas.length) return false

		for (var i = 0; i < areas.length; i++) {
			if (hasArtwork(areas[i])) return false
		}

		return true
	}

	/* -----------------------------------------------------------------------
	   Markup
	   -------------------------------------------------------------------- */

	// Inlined rather than rendered from `snippets/icon-search.liquid` and
	// `snippets/icon-close.liquid`: TIB is a theme app embed, so this whole
	// component is built at runtime and there is no Liquid in the preview
	// column to render a snippet into. The paths are the same drawings as those
	// snippets — only `aria-hidden`/`focusable` are added, because here the icon
	// sits next to its own label rather than standing alone.
	var ICON_SEARCH =
		'<svg class="icon icon-search" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
		'<path d="M14.1057 14.2L17 17M16.0667 9.53333C16.0667 13.1416 13.1416 16.0667 9.53333 16.0667C5.92507 16.0667 3 13.1416 3 9.53333C3 5.92507 5.92507 3 9.53333 3C13.1416 3 16.0667 5.92507 16.0667 9.53333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
		'</svg>'

	var ICON_CLOSE =
		'<svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">' +
		'<path d="M14.0625 3.9375L3.9375 14.0625" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
		'<path d="M14.0625 14.0625L3.9375 3.9375" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
		'</svg>'

	function escapeHtml(value) {
		return String(value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
	}

	/* -----------------------------------------------------------------------
	   The element
	   -------------------------------------------------------------------- */

	class EngravingCloseup extends HTMLElement {
		connectedCallback() {
			if (this.built) return
			this.built = true
			this.build()
		}

		build() {
			// Every string is a translation from `sections/main-product.liquid`.
			// There are deliberately no English fallbacks here: `start()` refuses to
			// mount without the strings, so an unlabelled control cannot exist and
			// this file holds no shopper-facing copy of its own.
			var strings = window.engravingCloseupStrings

			this.innerHTML =
				'<modal-opener class="engraving-closeup__opener no-js-hidden" data-modal="#' +
				MODAL_ID +
				'">' +
				'<button id="' +
				TRIGGER_ID +
				'" type="button" class="engraving-closeup__button button button--secondary focus-inset" aria-haspopup="dialog">' +
				ICON_SEARCH +
				'<span>' +
				escapeHtml(strings.button) +
				'</span>' +
				'</button>' +
				'</modal-opener>'

			// Built detached so that `<modal-dialog>` and its `[id^="ModalClose-"]`
			// child are complete before the custom element upgrades — ModalDialog's
			// constructor reads that button and throws without it.
			var dialog = document.createElement('div')
			dialog.innerHTML =
				'<modal-dialog id="' +
				MODAL_ID +
				'" class="product-popup-modal engraving-closeup-modal">' +
				'<div role="dialog" aria-modal="true" aria-label="' +
				escapeHtml(strings.heading) +
				'" class="product-popup-modal__content" tabindex="-1">' +
				'<button id="' +
				CLOSE_ID +
				'" type="button" class="product-popup-modal__close modal-close-button focus-inset" aria-label="' +
				escapeHtml(strings.close) +
				'">' +
				ICON_CLOSE +
				'</button>' +
				'<h2 class="product-popup-modal__title h4">' +
				escapeHtml(strings.heading) +
				'</h2>' +
				'<p class="engraving-closeup__hint">' +
				escapeHtml(strings.hint) +
				'</p>' +
				'<div class="engraving-closeup__viewport" role="img" aria-label="' +
				escapeHtml(strings.image_label) +
				'">' +
				'<div class="engraving-closeup__scaler" aria-hidden="true"></div>' +
				'</div>' +
				'</div>' +
				'</modal-dialog>'

			this.modal = dialog.firstElementChild
			// `ModalDialog.connectedCallback` re-parents itself to <body>; append it
			// somewhere connected first so that runs.
			document.body.appendChild(this.modal)

			this.viewport = this.modal.querySelector('.engraving-closeup__viewport')
			this.scaler = this.modal.querySelector('.engraving-closeup__scaler')

			// `open` is the attribute ModalDialog toggles in `show()`/`hide()`.
			// Watching it rather than the trigger's click means the close-up is
			// rendered no matter how it was opened, and torn down no matter how it
			// was closed — backdrop click, close button or Escape.
			new MutationObserver(this.onOpenChange.bind(this)).observe(this.modal, {
				attributes: true,
				attributeFilter: ['open'],
			})
		}

		onOpenChange() {
			if (this.modal.hasAttribute('open')) {
				this.render()
			} else {
				// Drop the clone rather than leave a second copy of the mockup (and a
				// second set of decoded images) resident for the rest of the session.
				this.scaler.textContent = ''
			}
		}

		/**
		 * Where the print area currently sits inside the close-up, in viewport
		 * coordinates. Measured off the clone as rendered rather than reused from
		 * the live element: the live mockup carries a `transform: scale()` from
		 * `section-tee-in-blue.css` which the clone — sitting under <body>, not
		 * under `.product__main.tee-gallery-wrapper` — does not inherit, and
		 * measuring the copy means that difference never has to be reasoned about.
		 */
		measureClone(clone, source) {
			var bounds = artworkBounds(clone)
			if (bounds && bounds.width && bounds.height) return bounds

			// The clone has not laid out yet. Fall back to the live element's
			// proportions, which are scale invariant: a uniform scale about an
			// element's centre moves the element and its descendants by the same
			// factor, so the print area occupies the same fraction of the mockup box
			// whether or not that scale is applied.
			var itemRect = source.item.getBoundingClientRect()
			var scalerRect = this.scaler.getBoundingClientRect()
			if (!itemRect.width || !itemRect.height || !scalerRect.width) return null

			return {
				left:
					scalerRect.left +
					((source.bounds.left - itemRect.left) / itemRect.width) *
						scalerRect.width,
				top:
					scalerRect.top +
					((source.bounds.top - itemRect.top) / itemRect.height) *
						scalerRect.height,
				width: (source.bounds.width / itemRect.width) * scalerRect.width,
				height: (source.bounds.height / itemRect.height) * scalerRect.height,
			}
		}

		render() {
			if (!this.modal.hasAttribute('open')) return

			var source = readSource()
			if (!source) {
				// The shopper has turned the engraving off: there is nothing left to
				// proofread and the trigger is about to disappear, so close rather than
				// leave a magnified blank patch of product on screen.
				if (engravingRemoved()) this.modal.hide()
				// Otherwise TIB is mid-re-render, or its stage has collapsed after a
				// resize. Hold the last good frame; the next mutation brings it back.
				return
			}

			this.scaler.style.transform = ''
			this.scaler.style.width = source.item.offsetWidth + 'px'
			this.scaler.style.height = source.item.offsetHeight + 'px'

			var clone = source.item.cloneNode(true)
			// Ids inside the clone would be duplicates of TIB's live ones. Nothing in
			// the mockup subtree references them (the artwork `<use>` points at a
			// document-level element outside it), so stripping them is free.
			clone.removeAttribute('id')
			var ided = clone.querySelectorAll('[id]')
			for (var i = 0; i < ided.length; i++) ided[i].removeAttribute('id')

			this.scaler.textContent = ''
			this.scaler.appendChild(clone)

			var bounds = this.measureClone(clone, source)
			if (!bounds) return

			var bleed =
				parseFloat(
					getComputedStyle(this.viewport).getPropertyValue(
						'--mm-engraving-closeup-bleed'
					)
				) || 0
			var spread = 1 + 2 * bleed

			var stageWidth = this.viewport.clientWidth
			if (!stageWidth) return

			var printWidth = stageWidth / spread
			var scale = printWidth / bounds.width
			var stageHeight = bounds.height * scale * spread

			this.viewport.style.height = stageHeight + 'px'

			// Giving the stage a height re-flows the dialog panel, which is centred on
			// the viewport and therefore *moves* when it changes size — taking the
			// clone with it. Everything positional has to be read again on the far
			// side of that, or the crop lands vertically off by half the growth.
			// Sizes are unaffected, so `scale` and the stage height still hold.
			bounds = this.measureClone(clone, source) || bounds
			var stageRect = this.viewport.getBoundingClientRect()
			var scalerRect = this.scaler.getBoundingClientRect()

			// With `transform-origin: 0 0`, a point currently at viewport coordinate
			// `p` lands at `origin + t + scale * (p - origin)`. Solve for the `t`
			// that puts the print area's centre at the stage's centre.
			var originX = scalerRect.left
			var originY = scalerRect.top
			var centreX = bounds.left + bounds.width / 2
			var centreY = bounds.top + bounds.height / 2
			var tx =
				stageRect.left + stageRect.width / 2 - originX - scale * (centreX - originX)
			var ty =
				stageRect.top + stageRect.height / 2 - originY - scale * (centreY - originY)

			this.scaler.style.transform =
				'translate(' + tx + 'px, ' + ty + 'px) scale(' + scale + ')'
		}
	}

	if (!window.customElements.get('engraving-closeup')) {
		window.customElements.define('engraving-closeup', EngravingCloseup)
	}

	/* -----------------------------------------------------------------------
	   Mounting
	   -------------------------------------------------------------------- */

	function start() {
		// No copy, no control. Every shopper-facing string is a translation handed
		// across from `sections/main-product.liquid`; without them the only thing
		// that could be rendered is an unlabelled button.
		if (!window.engravingCloseupStrings) return

		var column = document.querySelector(COLUMN_SELECTOR)
		if (!column) return

		var control = null
		var artworkObserver = null
		var schedule = frameDebounce(sync)

		// Attached as soon as TIB's staging container turns up rather than in one
		// shot at load: TIB injects it, so on a slow connection it can arrive after
		// this script runs. Every `sync()` retries until it lands.
		function watchArtwork() {
			if (artworkObserver) return
			var root = document.querySelector(TIB_ROOT_SELECTOR)
			if (!root) return
			artworkObserver = new MutationObserver(schedule)
			artworkObserver.observe(root, { subtree: true, childList: true })
		}

		function sync() {
			watchArtwork()

			var source = readSource()

			if (!source) {
				// No TIB, no gallery takeover, no engraving, or geometry that cannot be
				// read. Either the control was never created — the case on every
				// non-personalized product — or it is hidden until the DOM says
				// otherwise. Never a dead button.
				if (control) {
					control.setAttribute('hidden', '')
					control.render()
				}
				return
			}

			if (!control) control = document.createElement('engraving-closeup')

			// Re-home the control if TIB has re-rendered the column out from under
			// it. The element keeps its state — it is the same instance, so
			// `connectedCallback`'s `built` guard stops it rebuilding the dialog.
			// `.tee-gallery` is the sticky preview box; `.tee-gallery-content`
			// around it is only the runway TIB sizes to the form so the preview
			// stays pinned while the form scrolls. Mounting after the runway puts
			// the trigger at the bottom of the scroll track rather than under the
			// urn — 703px adrift on `Text + Clipart`, whose form is far taller than
			// the preview. Inside the sticky box it travels with the preview.
			var gallery = column.querySelector('.tee-gallery')
			if (!control.isConnected || (gallery && control.parentNode !== gallery)) {
				if (gallery) {
					gallery.appendChild(control)
				} else if (!column.contains(control)) {
					// TIB present but not yet laid out. Park it in the column so the
					// control is never orphaned; the next mutation re-homes it.
					column.appendChild(control)
				}
			}

			control.removeAttribute('hidden')
			// One re-render path for everything: the element itself no-ops unless the
			// close-up is actually open, so this is free while it is closed.
			control.render()
		}

		schedule()

		// Scoped to the preview column rather than the document, so on a product
		// where TIB never loads this callback never fires.
		new MutationObserver(schedule).observe(column, {
			subtree: true,
			childList: true,
			attributes: true,
			attributeFilter: ['style', 'class'],
		})

		window.addEventListener('load', schedule)
		window.addEventListener('resize', schedule)
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', start)
	} else {
		start()
	}
})()
